import styled from '@emotion/styled';
import { useResource, type IResource } from '@landsbankinn/l16n';
import {
  Box,
  Col,
  FlexButton,
  Row,
  Span,
  Tab,
  TabList,
  Tabs,
  TabsComposite,
} from '@landsbankinn/ui-kit';
import { Route, Routes } from 'react-router';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  AllowedLoginType,
  useApplicationClientContext,
  useApplicationServerContext,
  type ApplicationClientActionType,
  type LoginServerState,
} from 'src/state';
import { useOnScreenKeyboardDetector } from '../../hooks/useOnScreenKeyboardDetector';
import { Layout } from '../Layout';
import { App } from './Form/App/App';
import { AppPending } from './Form/App/AppPending';
import { AudkenniApp } from './Form/AudkenniApp/AudkenniApp';
import { AudkenniAppCode } from './Form/AudkenniApp/AudkenniAppCode';
import { LoginError } from './Form/LoginError';
import { Password } from './Form/Password/Password';
import { SmsForm } from './Form/Password/SmsForm';
import { PkiSim } from './Form/PkiSim/PkiSim';
import { PkiSimCode } from './Form/PkiSim/PkiSimCode';
import { loginRes } from './Login.res';
import { LoginHeaders } from './LoginHeaders';
import {
  getCorrectLoginTypeOr,
  getOrderedLoginTypes,
  getPreferredLoginTypeBasedOnAllowedLoginTypes,
  setLoginTypeAndReset,
} from './utils';

const StyledTabList = styled(TabList)`
  && [role='tablist'] > button[aria-selected='false'] {
    opacity: 0.6;
  }

  & :has(> [role='tablist']) {
    width: 100%;
  }
  & [role='tablist'] {
    justify-content: space-between;
    overflow-x: unset;
    min-width: unset;
    height: 4.7rem;
  }
  & {
    > div {
      > div:first-child {
        height: 5.3rem;
      }
    }
  }
  & [role='tablist'] > button + button {
    margin-left: 0;
  }
  & [role='tablist'] > button {
    padding: 0;
    font-weight: normal;
    flex-grow: 0;
    height: 4.7rem;
  }
  & button > span:last-child {
    font-weight: normal;
  }
`;

function TabItem({
  loginType,
  currentType,
  clientDispatch,
  r,
}: {
  loginType: AllowedLoginType;
  currentType: AllowedLoginType;
  clientDispatch: React.Dispatch<ApplicationClientActionType>;
  r: (resources: IResource, count?: number) => string;
}) {
  return (
    <TabsComposite.Trigger asChild value={currentType}>
      <FlexButton
        width="100%"
        paddingHorizontal="20"
        paddingTop="11"
        paddingBottom="9"
        border="regular"
        mainAxisAlignment="center"
        background={loginType === currentType ? 'blue main' : undefined}
        color={loginType === currentType ? 'white font' : undefined}
        onPointerDown={() =>
          setLoginTypeAndReset(currentType, loginType, clientDispatch)
        }
      >
        <Span fontVariant="label">{r(loginRes[`auth_${currentType}`])}</Span>
      </FlexButton>
    </TabsComposite.Trigger>
  );
}

export function Login() {
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [clientState, clientDispatch] = useApplicationClientContext();

  const { r } = useResource();
  const loginType = getCorrectLoginTypeOr(
    clientState,
    serverState,
    getPreferredLoginTypeBasedOnAllowedLoginTypes(serverState),
  );

  const keyBoardState = useOnScreenKeyboardDetector();

  const items = getOrderedLoginTypes(serverState).map((t) => {
    const instrumentMap: Record<AllowedLoginType, string> = {
      [AllowedLoginType.Password]: 'auth_tab_password',
      [AllowedLoginType.PkiSim]: 'auth_tab_pkisim',
      [AllowedLoginType.AudkenniApp]: 'auth_tab_audkenniapp',
      [AllowedLoginType.App]: 'auth_tab_app',
    };
    return {
      id: t,
      instrumentAs: instrumentMap[t],
      label: r(loginRes[`auth_${t}`]),
    };
  });

  const { isMobile } = useScreenSize();

  const allowedLoginTabs = items.map(({ id, instrumentAs, label }) => {
    return !isMobile ? (
      <Tab key={id} id={id} instrumentAs={instrumentAs}>
        {label}
      </Tab>
    ) : (
      <TabItem
        key={id}
        loginType={loginType}
        currentType={id}
        clientDispatch={clientDispatch}
        r={r}
      />
    );
  });
  return (
    <Layout>
      <Col mainAxisAlignment="start" height="100%">
        {clientState.errors && <LoginError />}
        {!clientState.errors && (
          <Routes>
            <Route path="/sms" element={<SmsForm />} />
            <Route path="/pkisim" element={<PkiSimCode />} />
            <Route path="/audkenniapp" element={<AudkenniAppCode />} />
            <Route path="/app" element={<AppPending />} />
            <Route
              path="/"
              element={
                <Col grow="1">
                  {!clientState.isSubmitting && (
                    <>
                      <LoginHeaders />
                      {!isMobile ? (
                        <Box paddingTop="36">
                          <Tabs<AllowedLoginType>
                            colorTheme="blue font"
                            selectedTabId={loginType}
                            setSelectedTabId={(val) =>
                              setLoginTypeAndReset(
                                val,
                                loginType,
                                clientDispatch,
                              )
                            }
                          >
                            <StyledTabList
                              variant="desktop"
                              fontSize="16"
                              height="36"
                            >
                              {allowedLoginTabs}
                            </StyledTabList>
                          </Tabs>
                        </Box>
                      ) : items.length > 1 ? (
                        <Col paddingTop="36" color="blue font">
                          <TabsComposite.Root
                            value={loginType}
                            onValueChange={(val: AllowedLoginType) =>
                              setLoginTypeAndReset(
                                val,
                                loginType,
                                clientDispatch,
                              )
                            }
                          >
                            <TabsComposite.List>
                              <Col rowGap="14">
                                <Row colGap="14">
                                  {allowedLoginTabs.slice(0, 2)}
                                </Row>
                                <Row colGap="14">
                                  {allowedLoginTabs.slice(2, 4)}
                                </Row>
                              </Col>
                            </TabsComposite.List>
                            <Box height="1" marginTop="28" borderTop="thin" />
                          </TabsComposite.Root>
                        </Col>
                      ) : null}
                    </>
                  )}
                  {loginType === AllowedLoginType.App && <App />}
                  {loginType === AllowedLoginType.PkiSim && (
                    <PkiSim loginButtonHidden={keyBoardState === 'visible'} />
                  )}
                  {loginType === AllowedLoginType.AudkenniApp && (
                    <AudkenniApp
                      loginButtonHidden={keyBoardState === 'visible'}
                    />
                  )}
                  {loginType === AllowedLoginType.Password && (
                    <Password loginButtonHidden={keyBoardState === 'visible'} />
                  )}
                </Col>
              }
            />
          </Routes>
        )}
      </Col>
    </Layout>
  );
}
